import { createServerFn } from '@tanstack/react-start';
import { isAxiosError } from 'axios';
import { z } from 'zod';

import { UserRole } from '@/features/manage-users/types/user-role';
import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { qtiApi } from '@/lib/http.server.ts';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import {
  assertSelfOrRole,
  assertUserAuthenticated,
  assertUserHasRole,
} from '@/middlewares/userAuthMiddleware';

import {
  CourseProgressResult,
  CourseResponse,
  CoursesResponse,
  CourseSyllabusResponse,
  FetchSyllabusResponse,
  FetchUsersResponse,
  QtiStimulusResponse,
  UserResponse,
} from './types';

export const fetchCourses = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      search: z.string().optional(),
    })
  )
  .handler(
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }) => {
      await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
      const { search } = data;

      const urlParams = new URLSearchParams();
      urlParams.set('filter', "status='active'");
      urlParams.set('sort', 'title');
      urlParams.set('limit', '50');
      if (search) {
        urlParams.set('search', search);
      }
      const response = await oneRosterApi.get<CoursesResponse>(
        `/ims/oneroster/rostering/v1p2/courses?${urlParams.toString()}`
      );
      return response.data.courses;
    }
  );

export const fetchCourse = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(z.object({ id: z.string() }))
  .handler(async ({ context: { oneRosterApi, cognitoUser }, data }) => {
    assertUserAuthenticated(cognitoUser);
    const { id } = data;
    const response = await oneRosterApi.get<CourseResponse>(
      `/ims/oneroster/rostering/v1p2/courses/${id}`
    );
    return response.data.course;
  });

export const fetchSyllabus = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      courseId: z.string(),
    })
  )
  .handler(
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }): Promise<FetchSyllabusResponse> => {
      await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
      try {
        const response = await oneRosterApi.get<CourseSyllabusResponse>(
          `/powerpath/syllabus/${data.courseId}`
        );
        return {
          success: true,
          data: response.data,
          error: null,
        };
      } catch (error) {
        console.error('Error fetching syllabus:', error);
        if (isAxiosError(error)) {
          return {
            success: false,
            error: error.response?.data,
            data: null,
          };
        }
        return {
          success: false,
          error: {
            message: 'Unknown error',
          },
          data: null,
        };
      }
    }
  );

export const fetchUser = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(z.object({ id: z.string() }))
  .handler(
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }) => {
      const { id } = data;
      await assertSelfOrRole(
        cognitoUser,
        oneRosterApiTyped,
        id,
        ELEVATED_ROLES
      );
      const response = await oneRosterApi.get<UserResponse>(
        `/ims/oneroster/rostering/v1p2/users/${id}`
      );
      return response.data.user;
    }
  );

export const fetchUsers = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      search: z.string().optional(),
    })
  )
  .handler(
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }) => {
      await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
      const { search } = data;
      const params = new URLSearchParams();
      params.set('filter', "status='active' AND enabledUser='true'");
      params.set('sort', 'givenName');
      params.set('limit', '50');
      if (search) {
        params.set('search', search);
      }

      const response = await oneRosterApi.get<FetchUsersResponse>(
        `/ims/oneroster/rostering/v1p2/users?${params.toString()}`
      );
      return response.data.users;
    }
  );

export const fetchUsersByRole = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      roles: z.array(z.enum(UserRole)).min(1, 'At least one role is required'),
      orgSourcedIds: z.array(z.string()).optional(),
      search: z.string().optional(),
      limit: z.number().optional().default(50),
      offset: z.number().optional().default(0),
      filter: z
        .string()
        .optional()
        .default("status='active' AND enabledUser='true'"),
      sort: z.string().optional().default('givenName'),
    })
  )
  .handler(
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }) => {
      await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
      const { roles, orgSourcedIds, search, limit, offset, filter, sort } =
        data;
      const params = new URLSearchParams();
      params.set('roles', roles.join(','));
      params.set('orgSourcedIds', orgSourcedIds?.join(',') || '');
      params.set('filter', filter);
      params.set('sort', sort);
      params.set('limit', limit.toString());
      params.set('offset', offset.toString());

      if (search) {
        params.set('search', search);
      }

      const response = await oneRosterApi.get<FetchUsersResponse>(
        `/edubridge/users?${params.toString()}`
      );
      return response.data.users;
    }
  );

export const fetchCourseProgress = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      courseId: z.string(),
      studentId: z.string(),
    })
  )
  .handler(
    // Ignore complex type issues with the server function
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    async ({
      context: { oneRosterApi, cognitoUser, oneRosterApiTyped },
      data,
    }): Promise<CourseProgressResult> => {
      await assertSelfOrRole(
        cognitoUser,
        oneRosterApiTyped,
        data.studentId,
        ELEVATED_ROLES
      );
      try {
        const response = await oneRosterApi.get(
          `/powerpath/lessonPlans/getCourseProgress/${data.courseId}/student/${data.studentId}`
        );
        return {
          success: true,
          data: response.data,
          error: null,
        };
      } catch (error) {
        console.error('Error fetching course progress:', error);
        if (isAxiosError(error)) {
          return {
            success: false,
            error: error.response?.data,
            data: null,
          };
        }
        return {
          success: false,
          error: {
            message: 'Unknown error',
          },
          data: null,
        };
      }
    }
  );

export const fetchQti = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      url: z.string(),
    })
  )
  .handler(async ({ context: { cognitoUser }, data }) => {
    assertUserAuthenticated(cognitoUser);
    try {
      const response = await qtiApi.get(data.url);
      const qtiData = response.data;
      return qtiData as unknown as QtiStimulusResponse;
    } catch (e) {
      console.error('Error fetching QTI:', e);
      throw e;
    }
  });
